import { animate, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideCheckCircle2,
  lucideCreditCard,
  lucideFileText,
  lucideHouse,
  lucideShield,
  lucideWifi,
  lucideWrench,
} from '@ng-icons/lucide';

import { ZardBadgeComponent } from '../../shared/components/badge';
import { ZardButtonComponent } from '../../shared/components/button';
import { ZardCardComponent } from '../../shared/components/card';

interface QuestionSummary {
  id: string;
  question: string;
  icon: string;
  answer: 'yes' | 'no' | 'skipped';
}

@Component({
  selector: 'app-questionnaire-complete',
  imports: [NgIcon, ZardBadgeComponent, ZardButtonComponent, ZardCardComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [
    provideIcons({
      lucideCheckCircle2,
      lucideShield,
      lucideHouse,
      lucideWifi,
      lucideWrench,
      lucideCreditCard,
      lucideFileText,
    }),
  ],
  animations: [
    trigger('fadeInUp', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(20px)' }),
        animate('400ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
    ]),
  ],
  template: `
    <div class="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div class="max-w-2xl w-full">
        <z-card @fadeInUp class="p-8 border-0 bg-white shadow-lg">
          <div class="flex justify-center mb-4">
            <div class="w-20 h-20 bg-[#00A878]/10 rounded-full flex items-center justify-center">
              <ng-icon name="lucideCheckCircle2" class="w-10 h-10 text-[#00A878]" />
            </div>
          </div>

          <div class="text-center mb-6">
            <h1 class="text-2xl text-[#1A2E47] font-bold mb-2">Félicitations !</h1>
            <p class="text-sm text-gray-600">
              Votre bien a été ajouté avec succès et toutes les informations ont été enregistrées.
            </p>
          </div>

          <z-card class="p-4 mb-6 bg-[#1A2E47]/5 border-[#1A2E47]/10">
            <div class="flex items-start gap-3">
              <div class="w-10 h-10 bg-[#1A2E47] rounded-lg flex items-center justify-center flex-shrink-0">
                <ng-icon name="lucideFileText" class="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 class="text-sm text-[#1A2E47] font-semibold mb-1">Votre bien est maintenant actif</h3>
                <p class="text-xs text-[#1A2E47]/70 leading-relaxed">
                  Vous pouvez maintenant gérer ce bien depuis votre tableau de bord, ajouter des locataires et suivre tous les paiements.
                </p>
              </div>
            </div>
          </z-card>

          <div class="border-t border-gray-200 my-6"></div>

          <div class="mb-6">
            <div class="text-center mb-4">
              <div class="inline-flex items-center justify-center w-16 h-16 bg-[#1A2E47] rounded-full mb-2 shadow-md">
                <span class="text-2xl text-white font-bold">{{ completionRate() }}%</span>
              </div>
              <h2 class="text-lg text-[#1A2E47] font-medium mb-1">Taux de configuration</h2>
              <p class="text-sm text-gray-600">
                {{ yesCount() }} service{{ yesCount() > 1 ? 's' : '' }} activé{{ yesCount() > 1 ? 's' : '' }} sur {{ totalQuestions }}
              </p>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
              @for (item of questionsSummary(); track item.id) {
                <div
                  class="flex items-center gap-3 p-3 bg-white rounded-lg border-2"
                  [style.borderColor]="item.answer === 'yes' ? '#00A878' : item.answer === 'no' ? '#1A2E47' : '#e5e7eb'"
                >
                  <div
                    class="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    [style.backgroundColor]="item.answer === 'yes' ? 'rgba(0, 168, 120, 0.1)' : item.answer === 'no' ? 'rgba(26, 46, 71, 0.1)' : '#f3f4f6'"
                  >
                    <ng-icon
                      [name]="item.icon"
                      class="w-4 h-4"
                      [style.color]="item.answer === 'yes' ? '#00A878' : item.answer === 'no' ? '#1A2E47' : '#9ca3af'"
                    />
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm text-gray-900 truncate">{{ item.question }}</p>
                  </div>
                  <z-badge
                    class="flex-shrink-0 text-xs border-0"
                    [style.backgroundColor]="item.answer === 'yes' ? '#00A878' : item.answer === 'no' ? '#1A2E47' : '#9ca3af'"
                  >
                    {{ item.answer === 'yes' ? 'Oui' : item.answer === 'no' ? 'Oui' : 'Ignoré' }}
                  </z-badge>
                </div>
              }
            </div>
          </div>

          <div class="border-t border-gray-200 my-6"></div>

          <div class="space-y-3 mb-6">
            <z-card class="p-3 border-0 bg-[#1A2E47]/5">
              <div class="flex items-start gap-2">
                <div class="w-5 h-5 bg-[#1A2E47] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span class="text-white text-xs font-bold">1</span>
                </div>
                <div class="flex-1">
                  <h3 class="text-sm text-[#1A2E47] font-semibold mb-1">Prochaines étapes</h3>
                  <p class="text-xs text-[#1A2E47]/70 leading-relaxed">
                    Accédez à votre tableau de bord pour gérer vos biens, consulter les statistiques financières, et suivre vos locataires.
                  </p>
                </div>
              </div>
            </z-card>

            @if (yesCount() < totalQuestions) {
              <z-card class="p-3 border-0 bg-yellow-50">
                <div class="flex items-start gap-2">
                  <div class="w-5 h-5 bg-yellow-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span class="text-white text-xs">💡</span>
                  </div>
                  <div class="flex-1">
                    <h3 class="text-sm text-yellow-900 mb-1">Services recommandés</h3>
                    <p class="text-xs text-yellow-700">
                      Vous pouvez toujours activer les services que vous avez ignorés depuis votre profil dans la section "Partenaires".
                    </p>
                  </div>
                </div>
              </z-card>
            }

            <z-card class="p-3 border-0 bg-[#00A878]/5">
              <div class="flex items-start gap-2">
                <div class="w-5 h-5 bg-[#00A878] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span class="text-white text-xs">✓</span>
                </div>
                <div class="flex-1">
                  <h3 class="text-sm text-[#00A878] font-bold mb-1">Assistance disponible</h3>
                  <p class="text-xs text-[#00A878]/80 leading-relaxed">
                    Notre équipe est à votre disposition pour vous aider à optimiser la gestion de votre bien. Contactez-nous depuis le menu "Support".
                  </p>
                </div>
              </div>
            </z-card>
          </div>

          <button z-button type="button" class="w-full bg-[#00A878] hover:bg-[#008f66] h-12 text-white shadow-md font-medium text-lg" (click)="accessDashboard.emit()">
            Accéder au tableau de bord
          </button>
        </z-card>

        <div class="flex items-center justify-center gap-2 mt-6">
          <img src="/logo.svg" alt="BAILIA" class="w-8 h-8" />
          <span class="text-[#1A2E47] font-bold tracking-wider">BAILIA</span>
        </div>
      </div>
    </div>
  `,
})
export class QuestionnaireCompleteComponent {
  readonly answers = input.required<Record<string, string>>();
  readonly accessDashboard = output<void>();

  protected readonly totalQuestions = 5;

  protected readonly questionsSummary = computed<QuestionSummary[]>(() => {
    const a = this.answers();
    const get = (id: string): 'yes' | 'no' | 'skipped' => {
      const value = a[id];
      if (value === 'yes' || value === 'no' || value === 'skipped') return value;
      return 'skipped';
    };
    return [
      { id: 'comprehensive_insurance', question: 'Assurance tous risques', icon: 'lucideShield', answer: get('comprehensive_insurance') },
      { id: 'vacant_insurance', question: 'Assurance non occupant', icon: 'lucideHouse', answer: get('vacant_insurance') },
      { id: 'internet_included', question: 'Internet inclus', icon: 'lucideWifi', answer: get('internet_included') },
      { id: 'trusted_artisans', question: 'Artisans de confiance', icon: 'lucideWrench', answer: get('trusted_artisans') },
      { id: 'rent_guarantee', question: 'Garantie loyers impayés', icon: 'lucideCreditCard', answer: get('rent_guarantee') },
    ];
  });

  protected readonly yesCount = computed(() => this.questionsSummary().filter(q => q.answer === 'yes').length);
  protected readonly completionRate = computed(() => Math.round((this.yesCount() / this.totalQuestions) * 100));
}
